import styled from "styled-components";
import { mobile, tablet } from "styles/breakpoints";

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

export const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

export const ModalHeader = styled.div`
  padding: 24px 24px 16px;
  border-bottom: 1px solid #dedee3;
  position: sticky;
  top: 0;
  background: #ffffff;
  z-index: 1;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
`;

export const ModalSubtitle = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 500;
  color: #6b6b76;
  line-height: 1.5;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #6b6b76;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: #f5f5f7;
    color: #1a1a2e;
  }

  &:focus {
    outline: 2px solid #798ecf;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ModalBody = styled.div`
  padding: 24px;
`;

export const FormSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h4`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 500;
  color: #1a1a2e;
`;

export const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: ${tablet}px) {
    flex-direction: row;
  }
`;

export const InputWrapper = styled.div`
  flex: 1;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #4a4d5e;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 500;
  border: 1px solid #dedee3;
  border-radius: 8px;
  background: #ffffff;
  color: #1a1a2e;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: #9999a6;
  }

  &:focus {
    outline: none;
    border-color: #798ecf;
    box-shadow: 0 0 0 3px rgba(121, 142, 207, 0.2);
  }

  &:disabled {
    background: #f5f5f7;
    cursor: not-allowed;
  }
`;

export const ReadOnlyValue = styled.div`
  padding: 12px 16px;
  font-size: 16px;
  font-weight: 500;
  border: 1px solid #dedee3;
  border-radius: 8px;
  background: #f9f9fb;
  color: #4a4d5e;
  box-sizing: border-box;
`;

export const AvailabilitySection = styled.div`
  margin-top: 8px;
`;

export const AnyTimeCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8ecf8 100%);
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: linear-gradient(135deg, #e8ecf8 0%, #dde3f4 100%);
  }
`;

export const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #798ecf;
`;

export const CheckboxLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #4a4d5e;
`;

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: ${mobile}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${tablet}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const DayCard = styled.div<{ $disabled?: boolean }>`
  border: 1px solid #dedee3;
  border-radius: 8px;
  overflow: hidden;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

export const DayHeader = styled.div`
  padding: 10px 12px;
  background: #f9f9fb;
  border-bottom: 1px solid #dedee3;
  font-size: 14px;
  font-weight: 500;
  color: #4a4d5e;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const DayAnyTimeLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 400;
  color: ${({ $disabled }) => ($disabled ? "#9999a6" : "#6b6b76")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  input {
    width: 14px;
    height: 14px;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    accent-color: #798ecf;
  }
`;

export const TimeSlots = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TimeSlot = styled.label<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  color: ${({ $selected }) => ($selected ? "#ffffff" : "#4a4d5e")};
  background: ${({ $selected }) => ($selected ? "#798ecf" : "transparent")};
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: ${({ $selected }) => ($selected ? "#6a7fc0" : "#f5f5f7")};
  }

  input {
    display: none;
  }
`;

export const ModalFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid #dedee3;
  background: #ffffff;

  @media (min-width: ${mobile}px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

export const CancelButton = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #6b6b76;
  background: transparent;
  border: 1px solid #dedee3;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background: #f5f5f7;
    border-color: #c9c9d1;
  }

  &:focus {
    outline: 2px solid #798ecf;
    outline-offset: 2px;
  }

  @media (min-width: ${mobile}px) {
    order: 1;
  }
`;

export const SubmitButton = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  background: linear-gradient(135deg, #798ecf 0%, #5a6fb0 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus {
    outline: 2px solid #798ecf;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (min-width: ${mobile}px) {
    order: 2;
  }
`;

// Sticky button styles - full-width bottom bar
export const StickyButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 12px 20px;
  background: #ffffff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (min-width: ${tablet}px) {
    padding: 14px 32px;
  }
`;

export const SelectionInfoRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

export const SelectionPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;
`;

export const SelectionCount = styled.span`
  font-weight: 700;
  color: #1a1a2e;
`;

export const SelectionSubtext = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b6b76;
`;

export const RequestCallButton = styled.button<{ disabled?: boolean }>`
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  color: #ffffff;
  background: ${({ disabled }) =>
    disabled
      ? "#B0B3C0"
      : "linear-gradient(135deg, #EC407A 0%, #E91E63 100%)"};
  border: none;
  border-radius: 999px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  box-shadow: ${({ disabled }) =>
    disabled ? "none" : "0 4px 16px rgba(233, 30, 99, 0.35)"};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${({ disabled }) =>
      disabled
        ? "#B0B3C0"
        : "linear-gradient(135deg, #F06292 0%, #EC407A 100%)"};
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0 6px 20px rgba(233, 30, 99, 0.45)"};
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
  }

  &:active {
    transform: ${({ disabled }) =>
      disabled ? "none" : "scale(0.98) translateY(0)"};
    box-shadow: ${({ disabled }) =>
      disabled ? "none" : "0 2px 12px rgba(233, 30, 99, 0.3)"};
  }

  &:focus {
    outline: ${({ disabled }) => (disabled ? "none" : "2px solid #EC407A")};
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ButtonIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

// Legacy - kept for backward compatibility
export const CountBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 700;
  line-height: 24px;
  color: #ffffff;
  background: linear-gradient(135deg, #EC407A 0%, #AD1457 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(173, 20, 87, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: badgePop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes badgePop {
    0% {
      transform: scale(0);
    }
    70% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

export const ErrorMessage = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 400;
  color: #af021c;
`;

export const DisclaimerText = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 400;
  color: #6b6b76;
  line-height: 1.5;
  font-style: italic;
`;

export const SuccessMessage = styled.div`
  text-align: center;
  padding: 32px 24px;
`;

export const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;

  svg {
    width: 32px;
    height: 32px;
  }
`;

export const SuccessTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
`;

export const SuccessText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #6b6b76;
  line-height: 1.5;
`;
